import React from 'react'
import { useTranslation } from 'react-i18next'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { CheckCircleFilledIcon, TaskStartIcon, DormantIcon } from '@/src/renderer/components/icons'
import { MiniCalendar } from './MiniCalendar'
import { PeriodOptions } from './PeriodOptions'
import { TimePointRow } from './TimePointRow'
import { WhenDropdownFooter } from './WhenDropdownFooter'
import { TodayIcon, TomorrowIcon, PeriodIcon, ClockIcon } from './WhenDropdownIcons'
import { isToday, isTomorrow } from './whenDropdownUtils'
import { formatStartDate } from '../../utils/parseStartDate'
import { useDateTimeDropdownState } from '../../hooks/useDateTimeDropdownState'
import type { WhenDropdownContentProps } from './WhenDropdown.types'
import './WhenDropdown.scss'

/**
 * WhenDropdownContent — Dropdown for selecting task start date/time.
 *
 * Two-column layout:
 * - Left: Day selection (Today/Tomorrow quick picks + calendar)
 * - Right: Time period or exact time selection
 *
 * Saves only on "Confirm" button click (unlike Attention/Duration which save immediately).
 */
export const WhenDropdownContent: React.FC<WhenDropdownContentProps> = ({
  value,
  onChange,
  onClose,
  onRemove,
  variant = 'actionbar',
}) => {
  const { t } = useTranslation()

  const state = useDateTimeDropdownState({
    value,
    onChange,
    onClose,
    formatDisplay: formatStartDate,
  })

  // ── Display icon for NL input ──
  let displayIcon: React.ReactNode = <TaskStartIcon size={16} />
  if (state.timeSource === 'time' && state.selectedTime) displayIcon = <ClockIcon size={16} />
  else if (state.timeSource === 'period' && state.selectedPeriod) displayIcon = <PeriodIcon period={state.selectedPeriod} size={16} />
  else if (state.daySource === 'quick' && state.selectedDate) {
    if (isToday(state.selectedDate)) displayIcon = <TodayIcon size={16} />
    else if (isTomorrow(state.selectedDate)) displayIcon = <TomorrowIcon size={16} />
  }

  return (
    <div className="when-dropdown" data-variant={variant} onKeyDown={state.handleGlobalKeyDown}>
      {/* NL Input */}
      <div className="when-dropdown__search-wrapper">
        <div className="when-dropdown__search">
          {displayIcon}
          <input
            ref={state.inputRef}
            className="when-dropdown__input"
            placeholder={t('taskCreation.actionStart')}
            value={state.inputValue ?? state.displayText}
            onChange={(e) => state.setInputValue(e.target.value)}
            onKeyDown={state.handleInputKeyDown}
            onFocus={() => { if (state.inputValue === null && state.displayText) state.setInputValue(state.displayText) }}
            autoFocus
          />
        </div>
      </div>

      {/* Two-column selection */}
      <div className="when-dropdown__selection">
        <div className="when-dropdown__columns">
          {/* Day Column */}
          <div className="when-dropdown__column when-dropdown__column--day">
            <div className="when-dropdown__column-label">
              {t('taskCreation.whenDayLabel')}
            </div>

            {/* Today */}
            <button
              type="button"
              className={`when-dropdown__item${state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) ? ' when-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(0)}
              onDoubleClick={() => {
                const d = new Date(); d.setHours(0, 0, 0, 0)
                onChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TodayIcon size={16} />
              <span className="when-dropdown__item-text">{t('taskCreation.whenToday')}</span>
              <div className="when-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isToday(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="when-dropdown__check" />
                )}
                <ShortcutHint>1</ShortcutHint>
              </div>
            </button>

            {/* Tomorrow */}
            <button
              type="button"
              className={`when-dropdown__item${state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) ? ' when-dropdown__item--selected' : ''}`}
              onClick={() => state.handleQuickDay(1)}
              onDoubleClick={() => {
                const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0)
                onChange({ date: d, time: null, period: null })
                onClose()
              }}
            >
              <TomorrowIcon size={16} />
              <span className="when-dropdown__item-text">{t('taskCreation.whenTomorrow')}</span>
              <div className="when-dropdown__item-right">
                {state.daySource === 'quick' && state.selectedDate && isTomorrow(state.selectedDate) && (
                  <CheckCircleFilledIcon size={16} className="when-dropdown__check" />
                )}
                <ShortcutHint>2</ShortcutHint>
              </div>
            </button>

            {/* Mini Calendar */}
            <MiniCalendar
              selectedDate={state.selectedDate}
              activeSelection={state.daySource === 'calendar'}
              onSelect={state.handleCalendarDay}
              onDoubleClick={(date) => {
                state.handleCalendarDay(date)
                onChange({ date, time: state.selectedTime || null, period: state.selectedPeriod })
                onClose()
              }}
            />
          </div>

          {/* Vertical separator */}
          <div className="when-dropdown__separator-vertical" />

          {/* Time Column */}
          <div className="when-dropdown__column when-dropdown__column--time">
            <div className="when-dropdown__column-label">
              {t('taskCreation.whenTimeLabel')}
            </div>

            <PeriodOptions
              selectedDate={state.selectedDate}
              selectedPeriod={state.selectedPeriod}
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              onPeriodSelect={state.handlePeriodSelect}
              onChange={onChange}
              onClose={onClose}
            />

            <TimePointRow
              selectedTime={state.selectedTime}
              timeSource={state.timeSource}
              hasSelection={state.hasSelection}
              onTimeChange={state.setSelectedTime}
              onApplyTime={state.applyTimeValue}
              onClearTime={state.handleTimeClear}
              onConfirm={state.handleConfirm}
              onClose={onClose}
            />

            <div className="when-dropdown__rest-section">
              <div className="context-menu-content__separator" />
              <button
                type="button"
                className="when-dropdown__item"
                disabled
                title={t('taskCreation.whenRestingDisabled')}
              >
                <DormantIcon size={16} />
                <span className="when-dropdown__item-text">{t('taskCreation.whenResting')}</span>
                <div className="when-dropdown__item-right">
                  <ShortcutHint>P</ShortcutHint>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <WhenDropdownFooter
        variant={variant}
        hasSelection={state.hasSelection}
        onConfirm={state.handleConfirm}
        onRemove={onRemove}
        onClose={onClose}
      />
    </div>
  )
}

// Re-export icons for use in TaskEntry badge
export { TodayIcon, TomorrowIcon, PeriodIcon } from './WhenDropdownIcons'
